import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useList, useCreate, useOne } from '@refinedev/core';
import { WizardLayout } from '@/components/wizard';
import { PricingTiersSection } from '@/components/pricing-tiers';
import { ChannelPassesFormFields, buildFormState } from './form';
import { type ChannelPass, type ChannelPassFormData, getChannelPassName } from './types';
import type { TranslationLanguage } from '@/components/translations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Radio, DollarSign } from 'lucide-react';
import { notify } from '@/lib/notify';

// ─── Types ──────────────────────────────────────────────────────────────────

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

interface TelegramChannelRecord {
  id: number;
  title: string;
  username: string | null;
}

interface TriggerRecord {
  id: number;
  name: string;
}

// ─── Steps ──────────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'basics', label: 'Основные данные' },
  { key: 'pricing', label: 'Тарифы' },
  { key: 'review', label: 'Обзор' },
];

// ─── ReviewStep ─────────────────────────────────────────────────────────────

interface ReviewStepProps {
  pass: ChannelPass;
  channels: TelegramChannelRecord[];
  onStepClick: (key: string) => void;
}

function ReviewStep({ pass, channels, onStepClick }: ReviewStepProps) {
  const name = getChannelPassName(pass);
  const channel = channels.find((c) => c.id === pass.channelId);
  const tiersCount = pass.pricingTiers?.length ?? 0;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Обзор</h2>
      <p className="text-sm text-muted-foreground">Проверьте данные перед завершением.</p>

      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('basics')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="h-4 w-4" />
              Основные данные
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <p><span className="text-muted-foreground">Название:</span> {name}</p>
          <p><span className="text-muted-foreground">Канал:</span> {channel?.title ?? `#${pass.channelId}`}</p>
          <p><span className="text-muted-foreground">Статус:</span> {pass.isActive ? 'Активен' : 'Неактивен'}</p>
          {pass.hasLimits && (
            <>
              {pass.userLimit !== null && (
                <p><span className="text-muted-foreground">Лимит покупок:</span> {pass.userLimit}</p>
              )}
              {pass.saleEndDate && (
                <p><span className="text-muted-foreground">Продажи до:</span> {new Date(pass.saleEndDate).toLocaleDateString('ru-RU')}</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('pricing')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Тарифы
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm">
          {tiersCount === 0 ? (
            <p className="text-muted-foreground">Тарифы не добавлены</p>
          ) : (
            <p>{tiersCount} тариф(ов)</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── ChannelPassWizard ──────────────────────────────────────────────────────

export function ChannelPassWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('basics');
  // After basics step, we create the channel pass as a draft and store its ID
  const [createdPassId, setCreatedPassId] = useState<number | null>(null);
  const submitRef = useRef<(() => void) | null>(null);

  // Languages
  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag ?? l.flagEmoji ?? '',
  }));

  // Channels
  const { result: channelsResult } = useList<TelegramChannelRecord>({
    resource: 'telegram-channels',
    pagination: { pageSize: 100 },
  });
  const channels = channelsResult?.data ?? [];

  // Triggers
  const { result: triggersResult } = useList<TriggerRecord>({
    resource: 'triggers',
    pagination: { pageSize: 100 },
  });
  const triggers = triggersResult?.data ?? [];

  // Fetch created pass data (for review)
  const { result: passData, query: passQuery } = useOne<ChannelPass>({
    resource: 'channel-passes',
    id: createdPassId ?? 0,
    queryOptions: { enabled: createdPassId !== null },
  });
  const pass = passData ?? null;

  // Create mutation
  const createMutation = useCreate<ChannelPass>();
  const isSubmitting = createMutation.mutation.isPending;

  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  const canProceed = (() => {
    if (currentStep === 'basics') { return true; } // validation happens in form submit
    if (currentStep === 'review') { return createdPassId !== null; }
    return true;
  })();

  const handleStepClick = (key: string) => {
    const targetIndex = STEPS.findIndex((s) => s.key === key);
    if (targetIndex <= stepIndex || (createdPassId !== null && targetIndex <= STEPS.length - 1)) {
      setCurrentStep(key);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 'basics') {
      submitRef.current?.();
      return;
    }
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEPS.length) {
      if (STEPS[nextIndex].key === 'review') {
        passQuery.refetch();
      }
      setCurrentStep(STEPS[nextIndex].key);
    }
  };

  const handlePrevStep = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].key);
    }
  };

  const handlePassDataSubmit = async (data: ChannelPassFormData) => {
    if (createdPassId) {
      // Already created — just advance (editing handled separately)
      setCurrentStep('pricing');
      return;
    }

    // First time — create channel pass
    createMutation.mutate({
      resource: 'channel-passes',
      values: { ...data, isActive: false },
    }, {
      onSuccess: (response) => {
        const created = response?.data;
        if (created?.id) {
          setCreatedPassId(created.id);
          setCurrentStep('pricing');
        }
      },
      onError: (error) => {
        notify.error(`Ошибка создания: ${error.message}`);
      },
    });
  };

  const handleComplete = () => {
    if (!createdPassId) { return; }
    navigate(`/channel-passes/show/${createdPassId}`);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <ChannelPassesFormFields
            key={`wizard-create-${createdPassId ?? 'new'}`}
            isEdit={createdPassId !== null}
            formLoading={false}
            initialState={pass ? buildFormState(pass) : buildFormState()}
            channels={channels}
            triggers={triggers}
            passId={createdPassId}
            languages={languages}
            embedded
            renderFooter={(submit) => {
              submitRef.current = submit;
              return null;
            }}
            onFinish={handlePassDataSubmit}
            onBack={() => navigate('/channel-passes')}
          />
        );

      case 'pricing':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Тарифы</h2>
              <p className="text-sm text-muted-foreground">
                Добавьте тарифы для канала. Этот шаг необязателен — тарифы можно добавить позже.
              </p>
            </div>
            {createdPassId && (
              <PricingTiersSection
                productKey={{ channelPassId: createdPassId }}
                languages={languages}
              />
            )}
          </div>
        );

      case 'review':
        return pass ? (
          <ReviewStep
            pass={pass}
            channels={channels}
            onStepClick={setCurrentStep}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <WizardLayout
      title="Создать подписку в канал"
      steps={STEPS}
      currentStep={currentStep}
      onStepClick={handleStepClick}
      onBack={() => navigate('/channel-passes')}
      onPrevStep={handlePrevStep}
      onNextStep={handleNextStep}
      onComplete={handleComplete}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      canProceed={canProceed}
      isSubmitting={isSubmitting}
    >
      {renderStep()}
    </WizardLayout>
  );
}
